import { useEffect, useState } from "react";
import Button from "../ui/Button";
import Spinner from "../ui/Spinner";
import masterService from "../../services/masterEntitiesService";
import { toast } from "react-toastify";
import { Sliders, Clock, ShieldAlert, KeyRound, Save } from "lucide-react";
import styles from "./PropertyIndustrialSettings.module.css";

export default function PropertyIndustrialSettings({ societyId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    escalation_host_minutes: 3,
    escalation_head_minutes: 6,
    final_expiry_minutes: 15,
    reassign_head_return_minutes: 3,
    draft_expiry_minutes: 5,
    override_daily_cap_per_guard: 5,
    otp_daily_cap_per_phone: 5,
    otp_daily_cap_per_property: 500,
    eod_checkout_time_ist: "23:59",
    vehicle_plate_regex: "^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$"
  });

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await masterService.getIndustrialSettings(societyId);
      if (data) {
        setSettings({
          escalation_host_minutes: data.escalation_host_minutes ?? 3,
          escalation_head_minutes: data.escalation_head_minutes ?? 6,
          final_expiry_minutes: data.final_expiry_minutes ?? 15,
          reassign_head_return_minutes: data.reassign_head_return_minutes ?? 3,
          draft_expiry_minutes: data.draft_expiry_minutes ?? 5,
          override_daily_cap_per_guard: data.override_daily_cap_per_guard ?? 5,
          otp_daily_cap_per_phone: data.otp_daily_cap_per_phone ?? 5,
          otp_daily_cap_per_property: data.otp_daily_cap_per_property ?? 500,
          eod_checkout_time_ist: data.eod_checkout_time_ist || "23:59",
          vehicle_plate_regex: data.vehicle_plate_regex || "^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$"
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load industrial property settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (societyId) {
      loadSettings();
    }
  }, [societyId]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await masterService.updateIndustrialSettings(societyId, {
        escalation_host_minutes: Number(settings.escalation_host_minutes),
        escalation_head_minutes: Number(settings.escalation_head_minutes),
        final_expiry_minutes: Number(settings.final_expiry_minutes),
        reassign_head_return_minutes: Number(settings.reassign_head_return_minutes),
        draft_expiry_minutes: Number(settings.draft_expiry_minutes),
        override_daily_cap_per_guard: Number(settings.override_daily_cap_per_guard),
        otp_daily_cap_per_phone: Number(settings.otp_daily_cap_per_phone),
        otp_daily_cap_per_property: Number(settings.otp_daily_cap_per_property),
        eod_checkout_time_ist: settings.eod_checkout_time_ist,
        vehicle_plate_regex: settings.vehicle_plate_regex
      });
      toast.success("Industrial settings updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={handleSave} className={styles.formCard}>
        <div className={styles.cardHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.iconWrap}>
              <Sliders size={20} color="#6366f1" />
            </div>
            <div>
              <h3 className={styles.title}>Industrial Intake & Escalation Configuration</h3>
              <p className={styles.subtitle}>
                Define operational timeouts, guard slip validity, rate-limiting caps, and validation rules.
              </p>
            </div>
          </div>
          <Button variant="primary" type="submit" disabled={saving}>
            <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        <div className={styles.sectionsWrapper}>
          {/* Section 1: Escalation Timeouts */}
          <div className={styles.box}>
            <div className={styles.boxTitle}>
              <Clock size={16} color="var(--color-dark-bg)" />
              Multi-Tier Escalation Timeouts
            </div>
            <p className={styles.boxDesc}>
              Controls automatic routing when host employees do not respond to arrival notifications.
            </p>

            <div className={styles.gridRow}>
              <div className={styles.field}>
                <label>Host Approval Timeout (mins)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  className={styles.input}
                  value={settings.escalation_host_minutes}
                  onChange={(e) => handleChange("escalation_host_minutes", e.target.value)}
                />
                <span className={styles.help}>Default: 3 mins. Escalates to Department Delegate.</span>
              </div>

              <div className={styles.field}>
                <label>Delegate Approval Timeout (mins)</label>
                <input
                  type="number"
                  min="2"
                  max="60"
                  className={styles.input}
                  value={settings.escalation_head_minutes}
                  onChange={(e) => handleChange("escalation_head_minutes", e.target.value)}
                />
                <span className={styles.help}>Default: 6 mins cumulative. Escalates to Floor Receptionist.</span>
              </div>

              <div className={styles.field}>
                <label>Final Request Expiry (mins)</label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  className={styles.input}
                  value={settings.final_expiry_minutes}
                  onChange={(e) => handleChange("final_expiry_minutes", e.target.value)}
                />
                <span className={styles.help}>Default: 15 mins. Request is auto-expired if no decision made.</span>
              </div>

              <div className={styles.field}>
                <label>Reassign Return to HOD (mins)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  className={styles.input}
                  value={settings.reassign_head_return_minutes}
                  onChange={(e) => handleChange("reassign_head_return_minutes", e.target.value)}
                />
                <span className={styles.help}>If reassigned to an employee who does not respond.</span>
              </div>
            </div>
          </div>

          {/* Section 2: Gate & Security Controls */}
          <div className={styles.box}>
            <div className={styles.boxTitle}>
              <ShieldAlert size={16} color="#d97706" />
              Gate & Guard Operations
            </div>
            <p className={styles.boxDesc}>
              Time-to-live for QR draft slips and supervisor override limits.
            </p>

            <div className={styles.gridRow}>
              <div className={styles.field}>
                <label>Vehicle Draft TTL (mins)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  className={styles.input}
                  value={settings.draft_expiry_minutes}
                  onChange={(e) => handleChange("draft_expiry_minutes", e.target.value)}
                />
                <span className={styles.help}>Default: 5 mins. Guard parking draft slips auto-expire after this window.</span>
              </div>

              <div className={styles.field}>
                <label>Supervisor Overrides / Guard / Day</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  className={styles.input}
                  value={settings.override_daily_cap_per_guard}
                  onChange={(e) => handleChange("override_daily_cap_per_guard", e.target.value)}
                />
                <span className={styles.help}>Maximum manual overrides allowed per guard per shift.</span>
              </div>

              <div className={styles.field}>
                <label>End-of-Day Auto-Checkout (IST)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={settings.eod_checkout_time_ist}
                  onChange={(e) => handleChange("eod_checkout_time_ist", e.target.value)}
                  placeholder="23:59"
                />
                <span className={styles.help}>Automated sweep time in Indian Standard Time (HH:mm).</span>
              </div>

              <div className={styles.field}>
                <label>Vehicle Plate Regex</label>
                <input
                  type="text"
                  className={styles.input}
                  value={settings.vehicle_plate_regex}
                  onChange={(e) => handleChange("vehicle_plate_regex", e.target.value)}
                />
                <span className={styles.help}>Indian RTO vehicle license plate pattern.</span>
              </div>
            </div>
          </div>

          {/* Section 3: Rate Limiting & OTP Caps */}
          <div className={styles.box}>
            <div className={styles.boxTitle}>
              <KeyRound size={16} color="#16a34a" />
              SMS & Verification Rate Limits
            </div>
            <p className={styles.boxDesc}>
              Protects against OTP SMS flooding and brute force attempts.
            </p>

            <div className={styles.gridRow}>
              <div className={styles.field}>
                <label>Daily OTP Cap per Phone</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className={styles.input}
                  value={settings.otp_daily_cap_per_phone}
                  onChange={(e) => handleChange("otp_daily_cap_per_phone", e.target.value)}
                />
                <span className={styles.help}>Maximum OTP attempts allowed for a single phone number per day.</span>
              </div>

              <div className={styles.field}>
                <label>Daily OTP Cap per Property</label>
                <input
                  type="number"
                  min="10"
                  max="5000"
                  className={styles.input}
                  value={settings.otp_daily_cap_per_property}
                  onChange={(e) => handleChange("otp_daily_cap_per_property", e.target.value)}
                />
                <span className={styles.help}>Property-wide daily aggregate cap before requiring manual gate registration.</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
